import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Target, Sparkles, ArrowRight } from 'lucide-react';
import api from '../../services/api';

/**
 * شريط تحديات أعلى الصفحة الرئيسية — يظهر فقط عند وجود تحديات متاحة (نفس هامش المحتوى).
 */
function StudentHomeChallengesBanner() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/challenges/my/available?page=1&limit=1');
        if (cancelled) return;
        if (!res.data?.success || !res.data.data) {
          setShow(false);
          return;
        }
        const total = res.data.data.pagination?.total;
        const list = res.data.data.challenges;
        const has =
          (typeof total === 'number' && total > 0) || (Array.isArray(list) && list.length > 0);
        setShow(has);
      } catch {
        if (!cancelled) setShow(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!show) return null;

  return (
    <div
      className="w-full min-w-0"
      aria-label={t('dashboard.student.homeChallengesBannerAria')}
    >
      <Link
        to="/student/challenges"
        className="group relative flex w-full flex-col gap-5 overflow-hidden rounded-[1.75rem] border border-white/25 bg-gradient-to-br from-violet-600 via-indigo-700 to-fuchsia-800 p-5 shadow-[0_22px_56px_-20px_rgba(91,33,182,0.55),0_0_0_1px_rgba(255,255,255,0.08)_inset] ring-1 ring-white/10 transition duration-300 hover:border-white/35 hover:shadow-[0_28px_64px_-18px_rgba(109,40,217,0.6)] hover:brightness-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/90 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:rounded-[2rem] sm:p-6 md:p-7"
      >
        {/* لمعان وعمق فوق التدرج */}
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_80%_at_10%_-20%,rgba(255,255,255,0.22),transparent_50%),radial-gradient(ellipse_70%_60%_at_100%_100%,rgba(251,191,36,0.18),transparent_55%),radial-gradient(ellipse_50%_45%_at_50%_100%,rgba(34,211,238,0.12),transparent_60%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-12 -top-24 h-56 w-56 rounded-full bg-fuchsia-400/35 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 -left-10 h-44 w-56 rounded-full bg-cyan-400/25 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
          aria-hidden
        />

        <div className="relative flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
          <div className="relative shrink-0">
            <div
              className="absolute -inset-1 rounded-[1.15rem] bg-gradient-to-br from-amber-300 via-fuchsia-400 to-cyan-300 opacity-40 blur-md transition duration-300 group-hover:opacity-55"
              aria-hidden
            />
            <div className="relative flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-[1rem] bg-gradient-to-br from-white/95 to-white/75 text-indigo-700 shadow-[0_12px_28px_-8px_rgba(0,0,0,0.25)] ring-2 ring-white/50 transition duration-300 group-hover:scale-[1.03] group-hover:shadow-[0_16px_36px_-10px_rgba(0,0,0,0.3)] sm:h-[4.75rem] sm:w-[4.75rem] sm:rounded-[1.1rem]">
              <Target className="h-[1.65rem] w-[1.65rem] sm:h-[1.85rem] sm:w-[1.85rem]" strokeWidth={2.25} aria-hidden />
            </div>
          </div>

          <div className="min-w-0 flex-1 text-start">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-white/35 bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-sm backdrop-blur-md sm:text-[11px]">
              <Sparkles className="h-3.5 w-3.5 text-amber-200" aria-hidden />
              {t('dashboard.student.homeChallengesBannerBadge')}
            </p>
            <h2 className="mt-2.5 text-lg font-extrabold leading-[1.2] tracking-tight text-white drop-shadow-sm sm:text-xl md:text-2xl">
              {t('dashboard.student.homeChallengesBannerTitle')}
            </h2>
            <p className="mt-1.5 max-w-2xl text-sm font-semibold leading-relaxed text-orange-200 drop-shadow-[0_0_16px_rgba(251,146,60,0.5)] sm:text-[0.9375rem]">
              {t('dashboard.student.homeChallengesBannerSubtitle')}
            </p>
          </div>
        </div>

        <div className="relative flex shrink-0 sm:items-center sm:justify-end">
          <span className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-indigo-800 shadow-[0_6px_20px_-6px_rgba(0,0,0,0.35)] ring-2 ring-white/80 transition duration-300 group-hover:bg-amber-50 group-hover:text-indigo-900 group-hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.4)] sm:w-auto sm:py-3.5 sm:pl-6 sm:pr-5">
            <span className="truncate">{t('dashboard.student.homeChallengesBannerCta')}</span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200/80 transition group-hover:bg-amber-100 group-hover:ring-amber-200/80">
              <ArrowRight
                className="h-4 w-4 transition duration-300 group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
                strokeWidth={2.5}
                aria-hidden
              />
            </span>
          </span>
        </div>
      </Link>
    </div>
  );
}

export default StudentHomeChallengesBanner;
